/**
 * The till: scan or search, pick a variant, sell for cash or store credit.
 *
 * The scan box is always focused and submits on Enter, because a USB barcode
 * scanner is just a keyboard that types a code and presses Enter. Scanning a
 * grab-bag variant barcode resolves to its owning item and preselects the
 * variant (issue I-39).
 *
 * A sale attempted with no connection is written to the outbox and reported as
 * saved-but-not-synced, rather than failing in front of a customer.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import * as api from '../lib/api';
import { enqueue } from '../lib/offline';
import { useInventory } from '../lib/useInventory';
import { useSession } from '../lib/session';
import ItemTable from '../components/ItemTable';

export default function SellPage() {
  const { db, setDb, databases, items, loading, stale, error, reload } = useInventory();
  const { refreshPending, can } = useSession();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [variantUpc, setVariantUpc] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [payment, setPayment] = useState('cash');
  const [customer, setCustomer] = useState('');
  const [orderName, setOrderName] = useState('');
  const [orders, setOrders] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const scanRef = useRef<HTMLInputElement>(null);

  const item = selected ? items[selected] : null;
  const variants = item?.variants ?? [];

  useEffect(() => {
    scanRef.current?.focus();
  }, []);

  useEffect(() => {
    void api
      .listOrders()
      .then((data) => setOrders(data.orders.map((order) => order.name)))
      .catch(() => setOrders([]));
  }, [db]);

  // Reset the per-sale fields whenever a different item is picked.
  useEffect(() => {
    if (!item) return;
    setVariantUpc(null);
    setQuantity(1);
    setPrice(String(item.price ?? 0));
  }, [item]);

  // A variant may carry its own price; fall back to the item's.
  useEffect(() => {
    if (!item) return;
    const variant = variants.find((entry) => entry.upc === variantUpc);
    const effective = variant && variant.price != null ? variant.price : item.price;
    setPrice(String(effective ?? 0));
  }, [variantUpc, item, variants]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const entries = Object.entries(items);
    if (!needle) return entries;
    // `tags:a, b` matches items carrying all of the listed tags, matching the
    // desktop app's search syntax.
    if (needle.startsWith('tags:')) {
      const wanted = needle
        .slice(5)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      return entries.filter(([, entry]) =>
        wanted.every((tag) =>
          (entry.tags ?? []).some((value) => String(value).toLowerCase() === tag),
        ),
      );
    }
    // Match the internal id, the name, and the manufacturer's UPC — a
    // seller reading the barcode off a case expects that to find the item.
    return entries.filter(
      ([upc, entry]) =>
        upc.includes(needle) ||
        (entry.name ?? '').toLowerCase().includes(needle) ||
        (entry.upc ?? '').toLowerCase().includes(needle),
    );
  }, [items, search]);

  /** Resolve a scanned code to an item, following grab-bag variant barcodes. */
  function handleScan(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;

    if (items[trimmed]) {
      setSelected(trimmed);
      setSearch('');
      return;
    }
    for (const [upc, entry] of Object.entries(items)) {
      const variant = (entry.variants ?? []).find((value) => value.upc === trimmed);
      if (variant) {
        setSelected(upc);
        setVariantUpc(variant.upc);
        setSearch('');
        setMessage(`Scanned variant: ${variant.label}`);
        return;
      }
    }
    setMessage(`No item found for ${trimmed}.`);
  }

  const unitPrice = Number(price) || 0;
  const lineTotal = unitPrice * quantity;

  /**
   * Sell immediately, or queue the sale when offline.
   *
   * The two writes (the sale, and the store-credit draw-down) are queued
   * independently. Wrapping both in one try/catch meant a network failure on
   * the *credit* call re-queued the **sale**, which had already succeeded —
   * selling the item twice once the outbox replayed.
   *
   * An insufficient balance is settled before anything is written, so the
   * operator is never told about it after the stock has already moved.
   */
  async function sell() {
    if (!item || !selected) return;
    const payload = {
      count: quantity,
      unit_price: unitPrice,
      payment_method: payment,
      variant_upc: variantUpc,
    };
    const payingWithCredit = payment === 'trade credit' && customer.trim() !== '';
    const creditName = customer.trim();

    // Decide about an overdraw up front, while nothing has been written yet.
    // If we cannot reach the server to check, fall through and queue: refusing
    // the sale because we could not read a balance would be worse.
    if (payingWithCredit) {
      try {
        const record = await api.getCredit(creditName);
        if (lineTotal > record.balance) {
          const proceed = window.confirm(
            `${creditName} has $${record.balance.toFixed(2)} in store credit, ` +
              `which does not cover $${lineTotal.toFixed(2)}.

` +
              'Record the sale anyway and let the balance go negative?',
          );
          if (!proceed) return;
        }
      } catch (err) {
        if (!(err as api.ApiError).isOffline) {
          setMessage((err as api.ApiError).message);
          return;
        }
      }
    }

    setBusy(true);
    setMessage('');
    const notes: string[] = [];
    let queued = false;

    try {
      await api.sellItem(db, selected, payload);
      notes.push(`Sold ${quantity} x ${item.name} for $${lineTotal.toFixed(2)}.`);
    } catch (err) {
      const apiError = err as api.ApiError;
      if (!apiError.isOffline) {
        setMessage(apiError.message);
        setBusy(false);
        scanRef.current?.focus();
        return;
      }
      await enqueue({
        path: `/items/${encodeURIComponent(selected)}/sell`,
        method: 'POST',
        body: { db_path: db, ...payload },
        label: `Sell ${quantity} x ${item.name}`,
      });
      queued = true;
      notes.push(
        `Offline: sale of ${quantity} x ${item.name} saved on this device.`,
      );
    }

    if (payingWithCredit) {
      const creditBody = {
        customer: creditName,
        amount: -lineTotal,
        reason: `sale: ${item.name}`,
        // The overdraw decision was already taken above.
        allow_overdraw: true,
      };
      try {
        const result = await api.postCredit(creditBody);
        notes.push(`${creditName} now has $${result.balance.toFixed(2)} credit.`);
      } catch (err) {
        const apiError = err as api.ApiError;
        if (apiError.isOffline) {
          await enqueue({
            path: '/credit',
            method: 'POST',
            body: creditBody,
            label: `Charge $${lineTotal.toFixed(2)} to ${creditName}`,
          });
          queued = true;
          notes.push('Store credit charge queued.');
        } else {
          notes.push(`Store credit not charged: ${apiError.message}`);
        }
      }
    }

    if (queued) {
      refreshPending();
      notes.push('Will sync when you reconnect.');
    }
    setMessage(notes.join(' '));
    setSelected(null);
    reload();
    setBusy(false);
    scanRef.current?.focus();
  }

  /** Add the current selection to an open order instead of selling it now. */
  async function addToOrder() {
    if (!item || !selected || !orderName) return;
    setBusy(true);
    setMessage('');
    const payload = {
      db_path: db,
      upc: selected,
      count: quantity,
      price: unitPrice,
      variant_upc: variantUpc,
    };
    try {
      await api.addOrderItem(orderName, payload);
      setMessage(`Added ${quantity} x ${item.name} to "${orderName}".`);
      setSelected(null);
    } catch (err) {
      const apiError = err as api.ApiError;
      if (apiError.isOffline) {
        await enqueue({
          path: `/orders/${encodeURIComponent(orderName)}/items`,
          method: 'POST',
          body: payload,
          label: `Add ${item.name} to ${orderName}`,
        });
        refreshPending();
        setMessage('Offline: queued for the order.');
        setSelected(null);
      } else {
        setMessage(apiError.message);
      }
    } finally {
      setBusy(false);
      scanRef.current?.focus();
    }
  }

  return (
    <div className="page sell-page">
      <div className="toolbar">
        <input
          ref={scanRef}
          className="scan"
          placeholder="Scan a barcode or search…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleScan(search);
            }
          }}
        />
        <select value={db} onChange={(event) => setDb(event.target.value)}>
          {databases.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <button type="button" className="ghost" onClick={reload}>
          Refresh
        </button>
      </div>

      {stale && (
        <p className="warn">
          Showing the last inventory saved on this device. Stock may have changed.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      {message && <p className="notice">{message}</p>}

      <div className="split">
        <ItemTable
          entries={filtered}
          loading={loading}
          selected={selected}
          onSelect={setSelected}
        />

        <aside className="card sell-panel">
          {!item ? (
            <p className="muted">Scan or select an item to sell.</p>
          ) : (
            <>
              <h2>{item.name}</h2>
              <p className="muted">
                ID {selected}
                {item.upc && item.upc !== selected ? ` · UPC ${item.upc}` : ''} ·{' '}
                {item.infinite ? '∞ in stock' : `${item.count} in stock`}
              </p>

              {variants.length > 0 && (
                <label>
                  Grab-bag variant
                  <select
                    value={variantUpc ?? ''}
                    onChange={(event) => setVariantUpc(event.target.value || null)}
                  >
                    <option value="">- none -</option>
                    {variants.map((variant) => (
                      <option key={variant.upc} value={variant.upc}>
                        {variant.label} ({variant.count} left)
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label>
                Quantity
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(event) => setQuantity(Math.max(1, Number(event.target.value)))}
                />
              </label>

              <label>
                Unit price
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                />
              </label>

              <label>
                Payment
                <select value={payment} onChange={(event) => setPayment(event.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="trade credit">Trade credit</option>
                </select>
              </label>

              {payment === 'trade credit' && (
                <label>
                  Customer (store credit)
                  <input
                    value={customer}
                    onChange={(event) => setCustomer(event.target.value)}
                    placeholder="Name on the credit account"
                  />
                </label>
              )}

              <p className="total">Total: ${lineTotal.toFixed(2)}</p>

              <button type="button" className="primary" disabled={busy} onClick={() => void sell()}>
                {busy ? 'Working…' : 'Sell now'}
              </button>

              {orders.length > 0 && can('sell') && (
                <div className="order-add">
                  <label>
                    Add to order
                    <select
                      value={orderName}
                      onChange={(event) => setOrderName(event.target.value)}
                    >
                      <option value="">- choose an order -</option>
                      {orders.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={busy || !orderName}
                    onClick={() => void addToOrder()}
                  >
                    Add to order
                  </button>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
